import { useAbortController } from '@packages/core/http';
import { useCallback, useEffect, useRef, useState } from 'react';

/** A read of a session's data, and the way to read it again. */
export type Ssm5Read<TSettled> = {
    /** Read again, whatever the last read found; does nothing while a read is in flight or without a session. */
    reload: () => void;
    /** Where the read stands. */
    state: Ssm5ReadState<TSettled>;
};

/** Where a read of a session's data stands: nothing to read, a read in flight, or how the last one settled. */
export type Ssm5ReadState<TSettled> =
    | TSettled
    | {
          /** Marks the absence of a session to read. */
          status: 'idle';
      }
    | {
          /** Marks a read in flight. */
          status: 'loading';
      };

/** How a read settles, given the Cloud Connector ID of the session and the signal that aborts it. */
export type Ssm5SettledRead<TSettled> = (cloudConnectorId: string, signal: AbortSignal) => Promise<TSettled>;

/** The least a settled read tells: how it settled, by a status of its own. */
type AnySettled = {
    /** How the read settled. */
    status: string;
};

const IDLE = { status: 'idle' } as const;
const LOADING = { status: 'loading' } as const;

/**
 * Read the data of the session behind a Cloud Connector ID: once per ID, again on reload, aborted when
 * the ID changes or the component unmounts. Each read replaces what the previous one found, so nothing
 * stale outlives a failure. The read is given the signal and settles with what it found or how it
 * failed; it never throws, so a read that is aborted is simply ignored.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session, or undefined without one
 * @param read - how the read settles; a function that does not change between renders
 * @returns where the read stands, and the reload
 */
export function useSsm5Read<TSettled extends AnySettled>(
    cloudConnectorId: string | undefined,
    read: Ssm5SettledRead<TSettled>
): Ssm5Read<TSettled> {
    const { abortSignalRef, signalAbort } = useAbortController();
    const [state, setState] = useState<Ssm5ReadState<TSettled>>(IDLE);
    const [readCount, setReadCount] = useState(0);
    const isReadingRef = useRef(false);

    useEffect(() => {
        isReadingRef.current = cloudConnectorId !== undefined;

        if (cloudConnectorId === undefined) {
            setState(IDLE);

            return;
        }

        const signal = abortSignalRef.current;

        setState(LOADING);
        read(cloudConnectorId, signal).then((settled) => {
            if (!signal.aborted) {
                isReadingRef.current = false;
                setState(settled);
            }
        });

        return signalAbort;
    }, [abortSignalRef, cloudConnectorId, read, readCount, signalAbort]);

    const reload = useCallback(() => {
        if (cloudConnectorId === undefined) {
            return;
        }

        if (isReadingRef.current) {
            return;
        }

        isReadingRef.current = true;
        setReadCount((count) => count + 1);
    }, [cloudConnectorId]);

    return { reload, state };
}
